import React from 'react';
import { translate } from '@utils/i18n';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import { getIssueTypeDisplayKey } from '../../utils/getIssueTypeDisplayKey';
const NS = 'features.iim_review.issues_grid';
const CARD_NS = 'features.iim_review.issue_card';
/** Converts snake_case to Title Case: low_ink_ratio → Low Ink Ratio */
function humanizeSnake(str) {
    return str.replace(/_/g, ' ').replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}
/**
 * Strips the "Title: detail" prefix and humanizes any snake_case tokens
 * in the description for display in the grid cell.
 */
function humanizeDescription(description) {
    const colonIdx = description.indexOf(': ');
    const detail = colonIdx !== -1 ? description.slice(colonIdx + 2) : description;
    // If the detail looks like a comma-separated snake_case list, humanize each token
    const tokens = detail.split(', ').map((t) => t.trim());
    const isTokenList = tokens.length >= 2 && tokens.every((t) => t.length <= 40 && !t.includes('. '));
    if (isTokenList) {
        return tokens.map(humanizeSnake).join(', ');
    }
    return detail;
}
function IssueTypeCell({ item }) {
    const displayKey = getIssueTypeDisplayKey(item);
    const label = translate(displayKey, { namespace: `${NS}.issue_type_labels`, defaultValue: item.issueType });
    return React.createElement("span", { className: "issues-grid__cell-text issues-grid__issue-type-link" }, label);
}
function IssueStatusCell({ item }) {
    const label = item.markedForSuggestion
        ? translate('correction_suggested', { namespace: CARD_NS })
        : translate(item.status, { namespace: `${CARD_NS}.status`, defaultValue: item.status });
    return React.createElement("span", { className: "issues-grid__cell-text" }, label);
}
function QuestionContextCell({ item }) {
    var _a, _b;
    if (item.question) {
        const questionText = ((_a = item.question.data) === null || _a === void 0 ? void 0 : _a.questionText) || '';
        const section = ((_b = item.question.data) === null || _b === void 0 ? void 0 : _b.section) || '';
        const prefix = section ? `${section}` : translate('question_context_fallback', { namespace: `${NS}.columns` });
        return (React.createElement("span", { className: "issues-grid__cell-text issues-grid__cell-text--truncate", title: `${prefix} - ${questionText}` },
            React.createElement("strong", null, prefix),
            questionText ? ` - ${questionText}` : ''));
    }
    // Humanize description to remove snake_case
    const display = humanizeDescription(item.description || '');
    return (React.createElement("span", { className: "issues-grid__cell-text issues-grid__cell-text--truncate", title: display }, display));
}
function DeviceInfoCell({ item }) {
    if (!item.inspectable) {
        return React.createElement("span", { className: "issues-grid__cell-text" });
    }
    const type = humanizeInspectableType(item.inspectable.type);
    const data = item.inspectable.data || {};
    const name = data.name || data.deviceName || data.device_name || '';
    // Use asset_type as fallback label if no name (e.g. pump curve: "Electric Fire Pump")
    const assetType = data.asset_type || data.assetType || '';
    const displayName = name || (assetType ? humanizeSnake(assetType) : '');
    const label = displayName ? `${type} · ${displayName}` : type;
    return (React.createElement("span", { className: "issues-grid__cell-text issues-grid__device-info", title: label },
        React.createElement("span", { className: "issues-grid__device-info-label" }, label)));
}
function ImpactCell({ item }) {
    var _a;
    const highImpact = (_a = item.issueData) === null || _a === void 0 ? void 0 : _a.highImpact;
    if (highImpact === null || highImpact === undefined) {
        return React.createElement("span", { className: "issues-grid__cell-text" }, translate('n_a', { namespace: `${NS}.impact` }));
    }
    const label = highImpact
        ? translate('high', { namespace: `${NS}.impact` })
        : translate('low', { namespace: `${NS}.impact` });
    return React.createElement("span", { className: "issues-grid__cell-text" }, label);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGridColumns() {
    return [
        {
            dataKey: 'issueType',
            label: translate('issue_type', { namespace: `${NS}.columns` }),
            columnType: 'primary',
            width: '18%',
            renderer: IssueTypeCell
        },
        {
            dataKey: 'status',
            label: translate('issue_status', { namespace: `${NS}.columns` }),
            width: '12%',
            renderer: IssueStatusCell
        },
        {
            dataKey: 'description',
            label: translate('question_context', { namespace: `${NS}.columns` }),
            width: '38%',
            renderer: QuestionContextCell
        },
        {
            dataKey: 'inspectable',
            label: translate('device_information', { namespace: `${NS}.columns` }),
            width: '24%',
            renderer: DeviceInfoCell
        },
        {
            dataKey: 'impact',
            label: translate('impact', { namespace: `${NS}.columns` }),
            width: '8%',
            renderer: ImpactCell
        }
    ];
}
